/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable no-script-url */
import React, { useState, useEffect, Suspense, lazy } from "react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";

import { useUser } from "~/Context/userContext";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Alert from "react-bootstrap/Alert";
import axios from "react-axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { postMessageBoardReply, postMessageBoardTopicClearTracker } from "~/api/getMessageBoard";
import { validateNonAuth, validateAuth } from "./TrackCommentValidationRules";
import useTrackCommentForm from "./useTrackCommentForm";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";

import "~/css/forms.less";

// Lazy load WYSIWYG components to reduce bundle size (only loaded when showWysiwyg is true)
const CommentPreview = lazy(() => import("./CommentPreview"));
const CommentToolbar = lazy(() => import("./CommentToolbar"));
const LinkEditor = lazy(() => import("./LinkEditor"));
const ImageEditor = lazy(() => import("./ImageEditor"));
const YouTubeEditor = lazy(() => import("./YouTubeEditor"));
// import { openFileUploader } from "~/Components/Elements/Global/FileUploader";

const closedComments = ["29376"];
const closedTopics = ["15110"];

export const Wrapper = styled.section`
  margin: 6px 0 6px 0;
`;
export const TipsWrapper = styled.section`
  display: inline;
  float: right;
`;
export const SignInLinkWrapper = styled.section`
  text-decoration: underline;
  display: inline-block;
  margin-left: 7px;
  &:hover {
    cursor: pointer;
    text-decoration: none;
  }
`;

const TrackCommentsForm = ({ track, topic, onReply, layoutCols = false, showWysiwyg = true }) => {
  const { state: userState, dispatch } = useUser();
  const [loading, setLoading] = useState(true);
  const [isLayoutCols, setIsLayoutCols] = useState(layoutCols);
  const [textareaRows, setTextareaRows] = useState(2);

  const [showMessage, setShowMessage] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const messageDisplayTime = 5000;
  const initialFormValues = { trackComment: "" };

  useEffect(() => {
    setLoading(!userState.loaded);
    setIsLayoutCols(layoutCols);
  }, [userState.loaded, layoutCols]);

  useEffect(() => {
    if (showMessage) {
      const timer = setTimeout(() => {
        setShowMessage(false);
      }, messageDisplayTime);
      return () => clearTimeout(timer);
    }
  }, [showMessage]);

  const validate = !loading && userState.loggedin ? validateAuth : validateNonAuth;

  const postComment = async () => {
    setLoading(true);

    const authorName = (inputs.author && inputs.author.trim()) || "";
    const author = userState.loggedin ? `${userState.user.dir}loggedin` : authorName;
    const trackId = (track && track.id) || undefined;
    const topicId = (topic && parseInt(topic.id, 10)) || undefined;

    const payload = {
      trackId,
      threadId: topicId,
      comment: inputs.trackComment.trim(),
      author,
    };
    const response = await postMessageBoardReply(axios, payload);
    const threadId = (response && response.data && response.data.thread_id) || undefined;

    // track the topic, you must be logged in and topic exists (might not if its the first reply)
    // only chimney will have this feature now
    if (userState.loggedin && userState.user && userState.isChim && threadId) {
      await postMessageBoardTopicClearTracker(axios, {
        threadId: threadId,
        chimDir: userState.user.dir,
      });
    }
    setLoading(false);

    if (response.status === 200) {
      setShowMessage(true);
      setShowPreview(false);
      clearForm();
      onReply(response.data.replies);
    }

  };

  const {
    inputs,
    handleInputChange,
    handleSubmit,
    insertLinkTag,
    insertImageTag,
    insertYouTubeTag,
    resizeImage,
    clearForm,
    errors,
    textareaRef,
    getSelectedText,
    captureSelectedText,
    selectedText,
    linkEditorOpen,
    setLinkEditorOpen,
    imageEditorOpen,
    setImageEditorOpen,
    youtubeEditorOpen,
    setYoutubeEditorOpen,
  } = useTrackCommentForm(initialFormValues, postComment, validate);

  // Adjust textarea height based on content (links, images, or youtube)
  useEffect(() => {
    const commentText = inputs.trackComment || "";
    const hasLink = commentText.includes("<a") || commentText.includes("<A");
    const hasImage = commentText.includes("<img") || commentText.includes("<IMG");
    const hasYouTube = commentText.includes("<iframe") || commentText.includes("<IFRAME");

    if (hasLink || hasImage || hasYouTube) {
      setTextareaRows(4); // Make taller when media is present
    } else {
      setTextareaRows(2); // Default height
    }
  }, [inputs.trackComment]);

  const handleLinkMouseDown = (e) => {
    // Capture selection on mousedown BEFORE the button click causes focus to change
    captureSelectedText();
  };

  const handleLinkClick = (e) => {
    e.preventDefault();
    setImageEditorOpen(false);
    setYoutubeEditorOpen(false);
    setLinkEditorOpen(!linkEditorOpen);
  };

  const handleImageClick = () => {
    setLinkEditorOpen(false);
    setYoutubeEditorOpen(false);
    setImageEditorOpen(!imageEditorOpen);
  };

  const handleYouTubeClick = () => {
    setLinkEditorOpen(false);
    setImageEditorOpen(false);
    setYoutubeEditorOpen(!youtubeEditorOpen);
  };

  const handleLinkInsert = (linkData) => {
    insertLinkTag(linkData);
    setShowPreview(true);
  };

  const handleImageInsert = (imageData) => {
    insertImageTag(imageData);
    setShowPreview(true);
  };

  const handleYouTubeInsert = (youtubeData) => {
    insertYouTubeTag(youtubeData);
    setShowPreview(true);
  };

  const handlePreviewClick = (e) => {
    e.preventDefault();
    setShowPreview((prev) => !prev);
  };

  const hasCommentContent = (inputs.trackComment || "").trim() !== "";

  // Hide preview if comment is cleared
  useEffect(() => {
    if (!hasCommentContent && showPreview) {
      setShowPreview(false);
    }
  }, [hasCommentContent, showPreview]);

  const openSignin = () => {
    if (!userState.loggedin) {
      dispatch({ type: "signin_open" });
      return;
    }
  };

  const isTrackClosed = track && closedComments.includes(track.id);
  const isTopicClosed = topic && closedTopics.includes(topic.id);

  if (isTrackClosed || isTopicClosed) {
    return (
      <div>
        <FontAwesomeIcon icon={faTimes} style={{ marginRight: 10, fontSize: 30 }} />
        Comments for this track are closed. <FontAwesomeIcon icon={faTimes} style={{ marginLeft: 10, fontSize: 30 }} />
      </div>
    );
  }
  return (
    <Wrapper>
      {loading && (
        <SkeletonTheme color="#fff" highlightColor="#f3f3f3">
          <Skeleton count={1} />
        </SkeletonTheme>
      )}

      {showMessage && <Alert variant="success">Thanks. Your comment was added below.</Alert>}

      {
        <Form
          className="totd-form search-form"
          style={isLayoutCols ? { marginTop: "16px" } : {}}
          noValidate
          onSubmit={(e) => handleSubmit(e)}
        >
          {!loading && !userState.loggedin && (
            <Form.Group as={Row} className={errors.author ? "error" : ""}>
              <Col md={isLayoutCols ? 12 : 6}>
                <div style={{ display: "flex" }}>
                  <Form.Control
                    disabled={loading}
                    name="author"
                    onChange={handleInputChange}
                    value={inputs.author || ""}
                    type="text"
                    autoComplete="off"
                    placeholder="Your Name"
                    style={{ width: "50%" }}
                  />
                  <span style={{ marginLeft: "16px", marginTop: "10px", fontSize: "12px" }}>
                    or <SignInLinkWrapper onClick={openSignin}>Login / Register</SignInLinkWrapper>
                  </span>
                </div>
                {errors.author && <span className="help error">{errors.author}</span>}
              </Col>
            </Form.Group>
          )}

          {userState.loggedin && !layoutCols && showWysiwyg && (
            <Suspense fallback={null}>
              {linkEditorOpen && (
                <LinkEditor
                  selectedText={selectedText}
                  onInsert={handleLinkInsert}
                  onCancel={() => setLinkEditorOpen(false)}
                />
              )}
              {imageEditorOpen && (
                <ImageEditor
                  onInsert={handleImageInsert}
                  onCancel={() => setImageEditorOpen(false)}
                />
              )}
              {youtubeEditorOpen && (
                <YouTubeEditor
                  onInsert={handleYouTubeInsert}
                  onCancel={() => setYoutubeEditorOpen(false)}
                />
              )}
            </Suspense>
          )}

          <Form.Group controlId="trackComment" className={errors.trackComment ? "error" : ""}>
            <Form.Control
              ref={textareaRef}
              disabled={loading}
              id="trackComment"
              name="trackComment"
              onChange={handleInputChange}
              value={inputs.trackComment || ""}
              as="textarea"
              rows={textareaRows}
              placeholder="Add a comment..."
            />
          </Form.Group>

          {userState.loggedin && !layoutCols && showWysiwyg && showPreview && hasCommentContent && (
            <Suspense fallback={null}>
              <CommentPreview content={inputs.trackComment || ""} onImageResize={resizeImage} />
            </Suspense>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "8px" }}>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {userState.loggedin && !layoutCols && showWysiwyg && (
                <Button
                  disabled={loading || !hasCommentContent}
                  variant={showPreview ? "primary" : "outline-secondary"}
                  type="button"
                  onClick={handlePreviewClick}
                  style={{ padding: "4px 10px", fontSize: "12px" }}
                >
                  {showPreview ? "Hide Preview" : "Preview"}
                </Button>
              )}
              <Button disabled={loading} variant="success" type="submit">
                Reply
              </Button>
            </div>
            {userState.loggedin && !layoutCols && showWysiwyg && (
              <Suspense fallback={null}>
                <CommentToolbar
                  onLinkClick={handleLinkClick}
                  onLinkMouseDown={handleLinkMouseDown}
                  onImageClick={handleImageClick}
                  onYouTubeClick={handleYouTubeClick}
                  linkActive={linkEditorOpen}
                  imageActive={imageEditorOpen}
                  youtubeActive={youtubeEditorOpen}
                />
              </Suspense>
            )}
          </div>
        </Form>
      }
    </Wrapper>
  );
};

export default TrackCommentsForm;
